import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { callGetStoreUrls, type StoreUrls } from './cloudFunctions';

/**
 * Cache local de las URLs del Play Store / App Store / Web.
 * Estas URLs se setean como variables de entorno en Firebase Functions
 * (PLAY_STORE_URL, IOS_STORE_URL, WEB_URL). Cuando publiques la app en
 * stores, solo seteas las vars + redeploy de functions — sin tocar código:
 *
 *   firebase functions:secrets:set PLAY_STORE_URL
 *   firebase functions:secrets:set IOS_STORE_URL
 *   firebase deploy --only functions
 *
 * El cache se actualiza cada 6 horas en background.
 */

const CACHE_KEY = 'GOSAFELY_STORE_URLS_V1';
const TTL_MS = 6 * 60 * 60 * 1000;
const FALLBACK: StoreUrls = {
  playStoreUrl: '',
  iosStoreUrl: '',
  webUrl: 'https://gosafely-3e0e7.web.app',
  published: { android: false, ios: false },
};

interface CacheEntry {
  data: StoreUrls;
  fetchedAt: number;
}

let memoryCache: CacheEntry | null = null;

async function readPersisted(): Promise<CacheEntry | null> {
  try {
    const raw = await AsyncStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.data && typeof parsed.fetchedAt === 'number') return parsed;
    return null;
  } catch {
    return null;
  }
}

async function persist(entry: CacheEntry) {
  try {
    await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* ignore */
  }
}

export const configService = {
  /**
   * Devuelve las URLs (desde cache si está fresco, sino fetcha al backend).
   * Nunca lanza — si todo falla devuelve el fallback.
   */
  async getStoreUrls(forceRefresh = false): Promise<StoreUrls> {
    const now = Date.now();
    if (!forceRefresh) {
      if (memoryCache && now - memoryCache.fetchedAt < TTL_MS) {
        return memoryCache.data;
      }
      if (!memoryCache) {
        const persisted = await readPersisted();
        if (persisted && now - persisted.fetchedAt < TTL_MS) {
          memoryCache = persisted;
          return persisted.data;
        }
      }
    }

    try {
      const resp = await callGetStoreUrls();
      const entry: CacheEntry = { data: resp.data, fetchedAt: now };
      memoryCache = entry;
      persist(entry).catch(() => {});
      return resp.data;
    } catch (e) {
      console.warn('[configService] getStoreUrls fallback:', e);
      // Si tenemos cache vieja, mejor servir eso que el fallback duro
      if (memoryCache) return memoryCache.data;
      const persisted = await readPersisted();
      if (persisted) return persisted.data;
      return FALLBACK;
    }
  },

  /**
   * URL preferida para la plataforma actual.
   * Si la app aún no se publicó en esa store, devuelve la URL web como fallback.
   */
  async getStoreUrlForCurrentPlatform(): Promise<string> {
    const urls = await this.getStoreUrls();
    if (Platform.OS === 'android' && urls.published.android) return urls.playStoreUrl;
    if (Platform.OS === 'ios' && urls.published.ios) return urls.iosStoreUrl;
    return urls.webUrl;
  },
};
